import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useAuthContext } from "providers/Auth";
import { Dispatch, SetStateAction, useState } from "react";
import { Link } from "react-router-dom";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";
import { Card } from "shared/components/Card";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { CriteriaRentType } from "shared/types";
import { ApplicationStatus, Money } from "shared/types/application";
import { formatToCurrency } from "shared/utils";
import { Property, Unit } from "types/landlord/address";
import { ApplicationRentSection } from "views/applications-id/components/LayoutHeader/components/ApplicationRentSection";
import { Stat } from "./components/Stat";
import { ScreeningStatusBadge } from "shared/app/ApplicationsTableView/components/ScreeningStatusBadge";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { HugeiconsIcon, IconSvgElement } from "@hugeicons/react";
import { UnavailableIcon } from "@hugeicons-pro/core-stroke-rounded";
import { ChangeDesiredMoveInDateModal } from "./components/ChangeDesiredMoveInDateModal";
import { Show, Button, ButtonSize, ButtonVariant } from "shared/components";
import { ChangeDesiredLeaseTermModal } from "./components/ChangeDesiredLeaseTermModal";

const formatPropertyAddress = (address: {
  address1: string;
  city: string;
  state: string;
  state_or_province: string;
  zip: string;
  postal_code: string;
}): string => {
  const state = address.state || address.state_or_province;
  const zip = address.zip || address.postal_code;
  const parts = [
    address.address1,
    address.city,
    [state, zip].filter(Boolean).join(" ")
  ].filter(Boolean);
  return parts.join(", ");
};

const statsGridClassName =
  "grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 xl:gap-x-6 2xl:gap-x-10 gap-y-6 p-6";

export const ApplicationOverview = ({
  applicationId,
  applicationRentAmount,
  setShowActivityLog,
  applicationStatus,
  latestApplicationRentHistory,
  unit,
  property,
  desiredMoveInDate,
  desiredLeaseTerm,
  leaseTermEnabled,
  screeningStatus,
  criteriaRentType,
  unitDelisted,
  refetchApplication,
  onViewTemplate,
  templateName
}: {
  applicationId: string;
  applicationRentAmount: {
    monthly_rent_amount?: Money;
    rent_amount?: Money;
    home_rent_amount?: Money;
    site_rent_amount?: Money;
    previous_rent_amount?: Money;
    housing_assistance_voucher_amount?: Money;
    pet_rent_amount?: Money;
  };
  setShowActivityLog: Dispatch<SetStateAction<boolean>>;
  applicationStatus: ApplicationStatus;
  latestApplicationRentHistory?: AdjustedApplicationRentActivity;
  unit: Unit;
  property: Property;
  desiredMoveInDate?: string;
  desiredLeaseTerm?: number;
  leaseTermEnabled: boolean;
  screeningStatus?: string;
  criteriaRentType: CriteriaRentType;
  unitDelisted?: boolean;
  refetchApplication: () => void;
  onViewTemplate?: () => void;
  templateName?: string;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { permissions, pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;
  const queryClient = useQueryClient();

  const [openedDesiredMoveInDateModal, setOpenedDesiredMoveInDateModal] =
    useState(false);
  const [openedDesiredLeaseTermModal, setOpenedDesiredLeaseTermModal] =
    useState(false);

  const desiredLeaseTermMonths = desiredLeaseTerm === 1 ? "month" : "months";
  const desiredLeaseTermLabel = desiredLeaseTerm
    ? `${desiredLeaseTerm} ${desiredLeaseTermMonths}`
    : "--";

  return (
    <Card className="w-full">
      <div className="border-b border-alpha-40 px-6 py-4">
        <ApplicationRentSection
          applicationId={applicationId}
          applicationRentAmount={applicationRentAmount}
          onViewDetails={() => setShowActivityLog(true)}
          canEdit={
            permissions.canEditApplicationRent &&
            [
              ApplicationStatus.Pending,
              ApplicationStatus.Started,
              ApplicationStatus.PartiallySubmitted,
              ApplicationStatus.Submitted
            ].includes(applicationStatus)
          }
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ["APPLICATION", applicationId]
            });
          }}
          latestApplicationRentHistory={latestApplicationRentHistory}
          criteriaRentType={criteriaRentType}
        />
      </div>

      {/* Property Information Row */}
      <div className={`${statsGridClassName} border-b border-alpha-40`}>
        <Stat
          label="Application status"
          value={
            <ApplicationStatusBadge
              type="application"
              status={applicationStatus}
              className="text-xs"
            />
          }
        />
        <Stat
          label="Screening status"
          value={
            screeningStatus ? (
              <ScreeningStatusBadge
                status={screeningStatus}
                className="text-xs"
              />
            ) : (
              "--"
            )
          }
        />
        <Stat
          label={`${dictionary.Property} name`}
          value={
            <Link
              to={`/properties/${property.id}`}
              className="text-base font-bold text-primary-500 hover:underline truncate block"
            >
              {property.name || "--"}
            </Link>
          }
        />
        <Stat
          label={`${dictionary.Property}`}
          value={
            property.address.address1 ? (
              <Tooltip placement="bottom-start">
                <TooltipTrigger className="block w-full min-w-0 text-left">
                  <p className="text-base font-medium text-content-1000 truncate">
                    {formatPropertyAddress(property.address)}
                  </p>
                </TooltipTrigger>
                <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white max-w-[250px]">
                  {formatPropertyAddress(property.address)}
                </TooltipContent>
              </Tooltip>
            ) : (
              "--"
            )
          }
        />
        <Stat
          label={`${dictionary.Unit}`}
          value={
            unitDelisted ? (
              <div className="flex items-start gap-2">
                <Tooltip placement="top">
                  <TooltipTrigger>
                    <div className="bg-danger-50 flex items-center justify-center p-1 rounded-2xl shrink-0">
                      <HugeiconsIcon
                        icon={UnavailableIcon as IconSvgElement}
                        size={12}
                        className="text-danger-500"
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="bg-alpha-800 rounded-lg border p-4 text-sm text-white max-w-[250px]">
                    Applications for this {dictionary.Unit.toLowerCase()} are no
                    longer allowed
                  </TooltipContent>
                </Tooltip>
                <Link
                  to={`/properties/${property.id}/units/${unit.id}`}
                  className="text-base font-bold text-primary-500 hover:underline"
                >
                  {unit.title || "-"}
                </Link>
              </div>
            ) : (
              <Link
                to={`/properties/${property.id}/units/${unit.id}`}
                className="text-base font-bold text-primary-500 hover:underline truncate block"
              >
                {unit.title || "--"}
              </Link>
            )
          }
        />
        <Stat
          label={`${dictionary.Property} group`}
          value={property.property_group?.name || "--"}
        />
      </div>

      {/* Rent, Dates & Template Row */}
      <div className={statsGridClassName}>
        <Stat
          label="Available"
          value={
            unit.available_at && moment(unit.available_at).isValid()
              ? moment(unit.available_at).utc().format("MMM D, YYYY")
              : "--"
          }
        />
        {!homeSiteRentEnabled ? (
          <Stat
            label="Listed rent"
            value={formatToCurrency({
              cents: unit.monthly_rent_amount?.cents || 0,
              currency: "USD"
            })}
          />
        ) : (
          <>
            <Stat
              label="Listed site rent"
              value={formatToCurrency({
                cents: unit.site_rent_amount?.cents || 0,
                currency: "USD"
              })}
            />
            <Stat
              label="Listed home rent"
              value={formatToCurrency({
                cents: unit.home_rent_amount?.cents || 0,
                currency: "USD"
              })}
            />
          </>
        )}
        <Stat
          label="Desired move-in"
          value={
            <div className="flex items-center gap-2">
              <p className="text-base font-medium text-content-1000 truncate">
                {desiredMoveInDate && moment(desiredMoveInDate).isValid()
                  ? moment(desiredMoveInDate).format("MMM D, YYYY")
                  : "--"}
              </p>
              <Show if={permissions.canChangeDesiredMoveInDate}>
                <Button
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.BASE}
                  onClick={() => setOpenedDesiredMoveInDateModal(true)}
                  className="shrink-0"
                >
                  Edit
                </Button>
              </Show>
            </div>
          }
        />
        <Show if={leaseTermEnabled}>
          <Stat
            label="Desired lease term"
            value={
              <div className="flex items-center gap-2">
                <p className="text-base font-medium text-content-1000 truncate">
                  {desiredLeaseTermLabel}
                </p>
                <Button
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.BASE}
                  onClick={() => setOpenedDesiredLeaseTermModal(true)}
                  className="shrink-0"
                >
                  Edit
                </Button>
              </div>
            }
          />
        </Show>
        <Show if={Boolean(templateName)}>
          <Stat
            className="lg:col-span-2"
            label="Screening template"
            value={
              <div className="flex items-center gap-2">
                <p className="text-base font-medium text-content-1000 truncate">
                  {templateName}
                </p>
                <Show
                  if={
                    Boolean(onViewTemplate) &&
                    applicationStatus === ApplicationStatus.Submitted
                  }
                >
                  <Button
                    variant={ButtonVariant.TEXT}
                    size={ButtonSize.BASE}
                    onClick={() => onViewTemplate?.()}
                    className="shrink-0"
                  >
                    Edit
                  </Button>
                </Show>
              </div>
            }
          />
        </Show>
      </div>
      <Show if={permissions.canChangeDesiredMoveInDate}>
        <ChangeDesiredMoveInDateModal
          opened={openedDesiredMoveInDateModal}
          onClose={() => setOpenedDesiredMoveInDateModal(false)}
          applicationId={applicationId}
          desiredMoveInDate={desiredMoveInDate || ""}
          refetchApplication={refetchApplication}
        />
      </Show>
      <ChangeDesiredLeaseTermModal
        opened={openedDesiredLeaseTermModal}
        onClose={() => setOpenedDesiredLeaseTermModal(false)}
        applicationId={applicationId}
        desiredLeaseTerm={desiredLeaseTerm || 0}
        refetchApplication={refetchApplication}
      />
    </Card>
  );
};
